import * as promises from '../../../shared/promises';
import * as _ from 'lodash';
import { Dispatch } from 'redux';
import { ProjectImportOptions, ServiceVCS } from '../../components/projects/import/types';
import { Repository, ServiceQueryParameters } from '../../types';
import { get, post } from '../api';
import {
  BULK_IMPORT_MAX,
  IMPORT_CHUNK_SIZE,
  VCS_DISCONNECT_SERVICE,
  VCS_FETCH_FILTERS,
  VCS_FETCH_INSTANCES,
  VCS_FETCH_REPOSITORIES,
  VCS_IMPORT_REPOSITORIES,
  VCS_IMPORT_REPOSITORIES_INCREMENT_GROUP_COUNT, VCS_SET_FILTER,
  VCS_RESET_IMPORT_STATUS,
  VCS_SELECT_REPOSITORY,
  VCS_SET_INSTANCE,
  VCS_SET_QUERY_PARAMETERS,
  VCS_SET_BRANCH_ON_REPOSITORY,
} from './types';

const actions = {
  setInstance: (instanceName: string) => ({
    type: VCS_SET_INSTANCE,
    payload: {
      instanceName,
    },
  }),
  setFilter: (filterType: string, filterValue: string) => ({
    type: VCS_SET_FILTER,
    payload: {
      filterType,
      filterValue,
    },
  }),
  setQueryParameters: (query: ServiceQueryParameters) => ({
    type: VCS_SET_QUERY_PARAMETERS,
    payload: {
      query,
    },
  }),
  setBranchOnRepository: (repo: Repository, branch: string) => ({
    type: VCS_SET_BRANCH_ON_REPOSITORY,
    payload: {
      repo,
      branch,
    },
  }),
  selectRepository: (repo: Repository) => ({
    type: VCS_SELECT_REPOSITORY,
    payload: {
      repo,
    },
  }),
  resetImportStatus: () => ({
    type: VCS_RESET_IMPORT_STATUS,
  }),
  fetchInstances: (service: ServiceVCS, defaultQueryParams?: string) => ({
    type: VCS_FETCH_INSTANCES,
    meta: {
      service,
      defaultQueryParams,
    },
    async payload() {
      return get(`/api/services/${service}/instances`, true);
    },
  }),
  fetchFilters: (service: ServiceVCS, instanceName: string, isRefetch: boolean = false) => ({
    type: VCS_FETCH_FILTERS,
    meta: {
      service,
      isRefetch,
    },
    async payload() {
      return get(`/api/services/${service}/filters`, true, { instanceName });
    },
  }),
  fetchRepositories: (service: ServiceVCS, query: ServiceQueryParameters, isRefetch: boolean = false) => ({
    type: VCS_FETCH_REPOSITORIES,
    meta: {
      service,
      isRefetch,
    },
    async payload() {
      return get(`/api/services/${service}/repositories`, true, query);
    },
  }),
  importRepositories: (
    service: ServiceVCS,
    repos: Repository[],
    options: ProjectImportOptions,
    instanceName: string,
    filterValue?: string,
  ) => async (dispatch: Dispatch) => {
    const isBulkImport = repos.length > BULK_IMPORT_MAX;
    // group the repos in chunks of size IMPORT_CHUNK_SIZE
    const chunkRange = _.range(Math.ceil(repos.length / IMPORT_CHUNK_SIZE));
    const importGroups = chunkRange.map(i =>
      repos.slice(i * IMPORT_CHUNK_SIZE, (i + 1) * IMPORT_CHUNK_SIZE));

    dispatch({
      type: `${VCS_IMPORT_REPOSITORIES}_PENDING`,
      meta: { service },
      payload: {
        isBulkImport,
        importGroupCount: importGroups.length,
        totalRepoCount: repos.length,
      },
    });

    try {
      const importedRepos = await promises.map(importGroups, async (group) => {
        const repo = await post(`/api/services/${service}/import`, {
          repos: group,
          options,
          instanceName,
          filterValue,
        });

        dispatch({
          type: VCS_IMPORT_REPOSITORIES_INCREMENT_GROUP_COUNT,
        });

        return repo.data;
      }, { concurrency: 10 });

      dispatch({
        type: `${VCS_IMPORT_REPOSITORIES}_FULFILLED`,
        meta: { service },
        payload: {
          importedRepos,
        },
      });
    } catch (err) {
      dispatch({
        type: `${VCS_IMPORT_REPOSITORIES}_REJECTED`,
        meta: { service },
        payload: err,
      });
    }
  },
  disconnectService: (service: ServiceVCS, instanceName: string) => ({
    type: VCS_DISCONNECT_SERVICE,
    meta: {
      service,
    },
    async payload() {
      return get(`/api/services/${service}/clearState`, true, { instanceName });
    },
  }),
};

export default actions;
